import {
  NUMBER_REQUEST_CREATION_KINDS,
  type NumberRequestKind,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  CheckCircleIcon,
  PhonePlusIcon,
  PhoneTransferIcon,
  PlugsConnectedIcon,
} from "@phosphor-icons/react";

/**
 * Switching between the three ways in, at any point.
 *
 * People routinely start one and realise they wanted another — usually after
 * reading the procedure below and discovering that porting means their existing
 * handset stops ringing unless we put a gateway in front of it, or that their
 * PBX makes the whole question moot. Making that a one-click change (rather
 * than "delete and start again") is the point, and the backend keeps any
 * document whose type survives the switch, so nothing already sent is lost.
 */
const ICONS: Record<NumberRequestKind, typeof PhonePlusIcon> = {
  new_number: PhonePlusIcon,
  port_existing: PhoneTransferIcon,
  sip_trunk: PlugsConnectedIcon,
};

export function KindSwitcher({
  kind,
  disabled,
  onChange,
}: {
  kind: NumberRequestKind;
  disabled?: boolean;
  onChange: (kind: NumberRequestKind) => void;
}) {
  const { t } = useTranslation("numberRequests");

  return (
    <>
      {/* Three across only once there is room for it: this now sits in the
            narrower of two columns. */}
      <div className="grid gap-3 xl:grid-cols-3">
        {/* "Keeping my number" is no longer offered: it only stays on the
            switcher of a request that already is one. */}
        {(kind === "port_existing"
          ? [kind, ...NUMBER_REQUEST_CREATION_KINDS]
          : NUMBER_REQUEST_CREATION_KINDS
        ).map((option) => {
          const Icon = ICONS[option];
          const selected = option === kind;

          return (
            <button
              key={option}
              type="button"
              disabled={disabled || selected}
              onClick={() => onChange(option)}
              className={cn(
                "relative flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors",
                selected
                  ? "border-primary bg-primary/5"
                  : "hover:bg-muted/50 disabled:opacity-60",
              )}
            >
              {selected && (
                <CheckCircleIcon
                  className="text-primary absolute top-3 right-3 h-5 w-5"
                  weight="fill"
                />
              )}

              <Icon
                className={cn(
                  "h-5 w-5 shrink-0",
                  selected ? "text-primary" : "text-muted-foreground",
                )}
              />

              <span className="min-w-0 pr-6">
                <span className="block text-sm font-medium">
                  {t(`numberRequests.kinds.${option}`)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}
